import { useEffect, useMemo, type ReactNode } from 'react';
import { App as AntApp, ConfigProvider as AntConfigProvider } from 'antd';
import { ThemeProvider } from 'styled-components';

import { titanBlueTheme, blueTheme } from '../styles';
import usePublicUiConfig from './hooks/usePublicUiConfig';
import { UiConfigContext, DEFAULT_UI_CONFIG, type IUiConfigContext } from './uiConfigContext';

interface IProps {
  children: ReactNode;
}

/**
 * Fetches the client app's branding/theme config (public, unauthenticated) and applies it as the
 * AntD + styled-components theme, document title and favicon.
 *
 * A failed/slow fetch never blocks the app — it falls back to the hardcoded defaults (the same
 * values the config is seeded with), so branding issues can't lock users out of login.
 */
const UiConfigProvider: React.FC<IProps> = ({ children }) => {
  const { uiConfig, errorFetchingUiConfig } = usePublicUiConfig();

  useEffect(() => {
    if (errorFetchingUiConfig) {
      console.error('Failed to load UI config, using defaults', errorFetchingUiConfig);
    }
  }, [errorFetchingUiConfig]);

  const contextValue = useMemo<IUiConfigContext>(
    () => ({
      sidebarTitle: uiConfig?.sidebarTitle ?? DEFAULT_UI_CONFIG.sidebarTitle,
      sidebarLogoUrl: uiConfig?.sidebarLogoUrl ?? DEFAULT_UI_CONFIG.sidebarLogoUrl,
      faviconUrl: uiConfig?.faviconUrl ?? DEFAULT_UI_CONFIG.faviconUrl,
      documentTitle: uiConfig?.documentTitle ?? DEFAULT_UI_CONFIG.documentTitle,
      footerText: uiConfig?.footerText ?? DEFAULT_UI_CONFIG.footerText,
    }),
    [uiConfig]
  );

  useEffect(() => {
    document.title = contextValue.documentTitle;
  }, [contextValue.documentTitle]);

  useEffect(() => {
    if (!contextValue.faviconUrl) return;

    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = contextValue.faviconUrl;
  }, [contextValue.faviconUrl]);

  const antdTheme = uiConfig?.theme?.antdTheme ?? titanBlueTheme;
  const styledTheme = uiConfig?.theme?.styledTheme ?? blueTheme;

  return (
    <AntConfigProvider theme={antdTheme}>
      <ThemeProvider theme={styledTheme}>
        <AntApp>
          <UiConfigContext.Provider value={contextValue}>{children}</UiConfigContext.Provider>
        </AntApp>
      </ThemeProvider>
    </AntConfigProvider>
  );
};

export default UiConfigProvider;
